import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/" className="text-purple-600 hover:underline text-sm mb-2 inline-block">
            ← Back to Home
          </Link>
          <h1 className="text-4xl font-bold text-gray-900">Terms of Service</h1>
          <p className="text-gray-600 mt-2">Last updated: November 9, 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
              Welcome to Book A Day. These Terms of Service ("Terms") govern your access to and use of Book A Day's platform,
              services, and applications (collectively, the "Services"). By accessing or using the Services, you agree to be
              bound by these Terms.
            </p>
            <p className="text-gray-700">
              Book A Day provides a platform that connects service experts with clients seeking to book appointments
              and services. The platform serves two distinct user types: (1) Experts who offer services and manage their
              schedules, and (2) Clients who book and receive services.
            </p>
          </section>

          {/* Definitions */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Definitions</h2>
            <ul className="space-y-3 text-gray-700">
              <li>
                <strong>"Expert"</strong> refers to individuals or businesses who offer services through the Book A Day platform,
                manage their availability, and accept bookings from Clients.
              </li>
              <li>
                <strong>"Client"</strong> refers to individuals who use Book A Day to browse, book, and pay for services
                offered by Experts.
              </li>
              <li>
                <strong>"Booking"</strong> refers to a scheduled appointment between a Client and a Expert for a specific service.
              </li>
              <li>
                <strong>"Services"</strong> includes the Book A Day platform, mobile applications, website, and all related features.
              </li>
            </ul>
          </section>

          {/* User Accounts */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">3.1 Account Creation</h3>
            <p className="text-gray-700 mb-4">
              To use certain features of the Services, you must create an account. You may register as either a Expert or a Client.
              You agree to provide accurate, current, and complete information during registration and to update such information
              to keep it accurate, current, and complete.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">3.2 Account Security</h3>
            <p className="text-gray-700 mb-4">
              You are responsible for maintaining the confidentiality of your account credentials and for all activities that
              occur under your account. You must immediately notify Book A Day of any unauthorized use of your account or any
              other breach of security. Book A Day will not be liable for any loss or damage arising from your failure to
              comply with this security obligation.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">3.3 Account Eligibility</h3>
            <p className="text-gray-700">
              You must be at least 18 years old to create an account and use the Services. By creating an account, you represent
              and warrant that you meet this age requirement and have the legal capacity to enter into these Terms.
            </p>
          </section>

          {/* Expert Terms */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Expert-Specific Terms</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">4.1 Service Offerings</h3>
            <p className="text-gray-700 mb-4">
              As a Expert, you are solely responsible for the quality, legality, and delivery of services you offer through
              the platform. You must accurately describe your services, including pricing, duration, and any requirements or
              restrictions.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">4.2 Availability and Scheduling</h3>
            <p className="text-gray-700 mb-4">
              You are responsible for maintaining accurate availability and working hours on the platform. You must honor all
              confirmed bookings or provide reasonable notice of cancellation in accordance with your cancellation policy.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">4.3 Professional Conduct</h3>
            <p className="text-gray-700 mb-4">
              Experts must maintain professional standards in all interactions with Clients. You must comply with all applicable
              laws, regulations, and professional licensing requirements related to the services you provide.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">4.4 Fees and Payments</h3>
            <p className="text-gray-700">
              Book A Day may charge service fees for use of the platform. All applicable fees will be clearly disclosed.
              You are responsible for all taxes associated with your services. Payment processing is handled through our
              third-party payment experts in accordance with their terms.
            </p>
          </section>

          {/* Client Terms */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Client-Specific Terms</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">5.1 Booking Services</h3>
            <p className="text-gray-700 mb-4">
              When you book a service through Book A Day, you enter into a direct agreement with the Expert. Book A Day
              facilitates the connection but is not a party to the service agreement between you and the Expert.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">5.2 Payment</h3>
            <p className="text-gray-700 mb-4">
              You agree to pay all fees for services you book through the platform. Payment terms, including deposits and
              cancellation fees, are set by individual Experts and will be clearly displayed before booking.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">5.3 Cancellations and Refunds</h3>
            <p className="text-gray-700">
              Cancellation and refund policies are set by individual Experts. You should review these policies carefully
              before booking. Book A Day is not responsible for enforcing Expert cancellation policies but may facilitate
              dispute resolution.
            </p>
          </section>

          {/* Prohibited Activities */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Prohibited Activities</h2>
            <p className="text-gray-700 mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Use the Services for any illegal purpose or in violation of any laws</li>
              <li>Impersonate any person or entity or falsely state or misrepresent your affiliation</li>
              <li>Interfere with or disrupt the Services or servers or networks connected to the Services</li>
              <li>Attempt to gain unauthorized access to any portion of the Services</li>
              <li>Use automated systems to access the Services without our permission</li>
              <li>Collect or harvest information about other users without their consent</li>
              <li>Post or transmit any harmful, threatening, abusive, or harassing content</li>
              <li>Circumvent payment processing or fees</li>
              <li>Create multiple accounts to manipulate reviews or ratings</li>
            </ul>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Intellectual Property</h2>
            <p className="text-gray-700 mb-4">
              The Services, including all content, features, and functionality, are owned by Book A Day and are protected by
              copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, sell, or lease
              any part of our Services without our express written permission.
            </p>
            <p className="text-gray-700">
              You retain ownership of any content you submit to the Services. By submitting content, you grant Book A Day a
              worldwide, non-exclusive, royalty-free license to use, reproduce, and display such content in connection with
              the Services.
            </p>
          </section>

          {/* Disclaimer of Warranties */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Disclaimer of Warranties</h2>
            <p className="text-gray-700 mb-4">
              THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
              INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p className="text-gray-700">
              Book A Day does not warrant that the Services will be uninterrupted, secure, or error-free. We do not endorse or
              guarantee the quality of services provided by Experts. You use the Services at your own risk.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-700 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, Book A Day SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY,
              OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </p>
            <p className="text-gray-700">
              Our total liability to you for all claims arising from or related to the Services shall not exceed the amount
              you paid to Book A Day in the twelve (12) months preceding the claim, or $100, whichever is greater.
            </p>
          </section>

          {/* Dispute Resolution */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Dispute Resolution</h2>
            <p className="text-gray-700 mb-4">
              If you have a dispute with another user (Expert or Client), you agree to first attempt to resolve the dispute
              directly with that user. If you are unable to resolve the dispute, you may contact Book A Day support for assistance.
            </p>
            <p className="text-gray-700">
              Any disputes between you and Book A Day shall be resolved through binding arbitration in accordance with the
              rules of the American Arbitration Association, except for disputes that qualify for small claims court.
            </p>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Changes to Terms</h2>
            <p className="text-gray-700">
              We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the
              updated Terms on the platform and updating the "Last Updated" date. Your continued use of the Services after
              such changes constitutes your acceptance of the modified Terms.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Termination</h2>
            <p className="text-gray-700 mb-4">
              You may terminate your account at any time through your account settings. Book A Day reserves the right to
              suspend or terminate your account and access to the Services at any time, with or without notice, for any
              reason, including violation of these Terms.
            </p>
            <p className="text-gray-700">
              Upon termination, your right to use the Services will immediately cease. All provisions of these Terms that
              by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers,
              and limitations of liability.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Information</h2>
            <p className="text-gray-700">
              If you have any questions about these Terms, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700">
                <strong>Book A Day Support</strong><br />
                Email: legal@bookday.me<br />
                Address: [Your Business Address]
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
